// Projektstand Service - CRUD for the Eisenhower matrix (projektstand table)
// Access: MF-Admin (GL) and MF-PL only

const PROJEKTSTAND_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projektstand",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const getBaseUrl = () =>
  `https://${PROJEKTSTAND_CONFIG.storageAccount}.table.core.windows.net/${PROJEKTSTAND_CONFIG.tableName}`;

const generateTopicId = () =>
  `topic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Ensure the projektstand table exists (created on first use)
const ensureTableExists = async () => {
  try {
    const url = `https://${PROJEKTSTAND_CONFIG.storageAccount}.table.core.windows.net/Tables?${PROJEKTSTAND_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: PROJEKTSTAND_CONFIG.tableName }),
    });
    return response.ok || response.status === 409;
  } catch {
    return false;
  }
};

// Load all topics for a project
export const getTopics = async (projectCode) => {
  try {
    const baseUrl = getBaseUrl();
    const filter = `&$filter=PartitionKey eq '${projectCode}'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}()?${PROJEKTSTAND_CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries
      .map((item) => ({
        id: item.RowKey,
        projectCode: item.PartitionKey,
        title: item.Title || "",
        text: item.Text || "",
        quadrant: item.Quadrant || "urgent-important",
        status: item.Status || "offen",
        sortOrder: parseInt(item.SortOrder) || 0,
        posX: parseFloat(item.PosX) || 5,
        posY: parseFloat(item.PosY) || 5,
        responses: item.Responses ? JSON.parse(item.Responses) : [],
        completedAt: item.CompletedAt || "",
        createdBy: item.CreatedBy || "",
        createdAt: item.CreatedAt || "",
        updatedAt: item.UpdatedAt || "",
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  } catch (error) {
    console.error("Fehler beim Laden der Projektstand-Themen:", error);
    return [];
  }
};

// Create a new topic
export const createTopic = async (projectCode, topicData) => {
  try {
    await ensureTableExists();

    const rowKey = generateTopicId();
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: projectCode,
      RowKey: rowKey,
      Title: topicData.title || "",
      Text: topicData.text || "",
      Quadrant: topicData.quadrant || "urgent-important",
      Status: topicData.status || "offen",
      SortOrder: topicData.sortOrder || 0,
      PosX: topicData.posX != null ? topicData.posX : 5,
      PosY: topicData.posY != null ? topicData.posY : 5,
      Responses: JSON.stringify(topicData.responses || []),
      CompletedAt: "",
      CreatedBy: topicData.createdBy || "",
      CreatedAt: now,
      UpdatedAt: now,
    };

    const url = `${getBaseUrl()}?${PROJEKTSTAND_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Create topic failed: ${response.status} - ${errorText}`);
    }

    return {
      id: rowKey,
      projectCode,
      title: topicData.title || "",
      text: topicData.text || "",
      quadrant: topicData.quadrant || "urgent-important",
      status: topicData.status || "offen",
      sortOrder: topicData.sortOrder || 0,
      posX: topicData.posX != null ? topicData.posX : 5,
      posY: topicData.posY != null ? topicData.posY : 5,
      responses: topicData.responses || [],
      completedAt: "",
      createdBy: topicData.createdBy || "",
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Fehler beim Erstellen des Themas:", error);
    throw error;
  }
};

// Update an existing topic
export const updateTopic = async (projectCode, topicId, topicData) => {
  try {
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: projectCode,
      RowKey: topicId,
      Title: topicData.title || "",
      Text: topicData.text || "",
      Quadrant: topicData.quadrant || "urgent-important",
      Status: topicData.status || "offen",
      SortOrder: topicData.sortOrder || 0,
      PosX: topicData.posX != null ? topicData.posX : 5,
      PosY: topicData.posY != null ? topicData.posY : 5,
      Responses: JSON.stringify(topicData.responses || []),
      CompletedAt: topicData.completedAt || "",
      CreatedBy: topicData.createdBy || "",
      CreatedAt: topicData.createdAt || now,
      UpdatedAt: now,
    };

    const url = `${getBaseUrl()}(PartitionKey='${projectCode}',RowKey='${topicId}')?${PROJEKTSTAND_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Update topic failed: ${response.status} - ${errorText}`);
    }

    return { id: topicId, projectCode, ...topicData, updatedAt: now };
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Themas:", error);
    throw error;
  }
};

// Delete a topic
export const deleteTopic = async (projectCode, topicId) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='${projectCode}',RowKey='${topicId}')?${PROJEKTSTAND_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Delete topic failed: ${response.status}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen des Themas:", error);
    throw error;
  }
};

// Batch update multiple topics (e.g. after drag-and-drop reorder)
export const updateTopicsBatch = async (projectCode, topics) => {
  try {
    await Promise.all(
      topics.map((topic) => updateTopic(projectCode, topic.id, topic))
    );
    return true;
  } catch (error) {
    console.error("Fehler beim Batch-Update der Themen:", error);
    throw error;
  }
};
